import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Star, Trash2, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { usePrefs } from "@/contexts/PrefsContext";

export const GalleryManager = ({ value, onChange, uploadPath = "/venue/uploads" }: { value: string[]; onChange: (g: string[]) => void; uploadPath?: string }) => {
  const { t } = usePrefs();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    const added: string[] = [];
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(uploadPath, fd);
        added.push(data.url);
      } catch (e) {
        toast.error(`${f.name}: ${errorText(e, t)}`);
      }
    }
    onChange([...value, ...added]);
    setBusy(false);
    if (input.current) input.current.value = "";
  };

  const makeCover = (k: number) => onChange([value[k], ...value.filter((_, j) => j !== k)]);
  const remove = (k: number) => onChange(value.filter((_, j) => j !== k));

  return (
    <div className="space-y-3" data-testid="gallery-manager">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {value.map((src, k) => (
          <div key={src + k} data-testid={`gallery-item-${k}`} className="group relative aspect-[4/3] overflow-hidden rounded-2xl border bg-muted">
            <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
            {k === 0 && <span className="absolute top-2 start-2 rounded-full bg-primary text-primary-foreground text-[11px] font-bold px-2 py-0.5">{t("edit.cover")}</span>}
            <div className="absolute inset-x-2 bottom-2 flex gap-1.5 justify-end">
              {k > 0 && (
                <button type="button" data-testid={`gallery-make-cover-${k}`} onClick={() => makeCover(k)} title={t("edit.makeCover")} className="h-9 w-9 grid place-items-center rounded-full bg-white/90 text-amber-600 shadow">
                  <Star className="h-4 w-4" />
                </button>
              )}
              <button type="button" data-testid={`gallery-remove-${k}`} onClick={() => remove(k)} title={t("edit.remove")} className="h-9 w-9 grid place-items-center rounded-full bg-white/90 text-rose-600 shadow">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="gallery-upload-button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="aspect-[4/3] rounded-2xl border-2 border-dashed border-primary/40 text-primary flex flex-col items-center justify-center gap-2 font-bold text-sm hover:bg-primary/5 disabled:opacity-60 transition-[background-color]"
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
          {busy ? t("edit.uploading") : t("edit.upload")}
        </button>
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="gallery-file-input" />
      <p className="text-xs text-muted-foreground">{t("edit.photosHint")}</p>
    </div>
  );
};
